import { LocationDetail } from '../models/Location';

export type PointType = 'wireless' | 'gps' | 'qrNfc';
export type LocationSort = 'alphabetical' | 'distance' | 'installationDate';
export interface Coordinates { lat: number; lng: number; }

export const POINT_TYPES: PointType[] = ['wireless', 'gps', 'qrNfc'];
const collator = new Intl.Collator('pl', { sensitivity: 'base', numeric: true });
const distanceFormatters = new Map<string, Intl.NumberFormat>();
const dateFormatters = new Map<string, Intl.DateTimeFormat>();
const text = (value: unknown): string => typeof value === 'string' ? value.trim()
    : typeof value === 'number' && Number.isFinite(value) ? String(value) : '';

export const getPointType = (id?: string): PointType | null => {
    const value = (id || '').trim();
    if (/^g[0-9a-f]+$/i.test(value)) return 'gps';
    if (/^s[0-9a-f]+$/i.test(value)) return 'qrNfc';
    return /^[0-9a-f]+$/i.test(value) ? 'wireless' : null;
};

export const parsePublicLocations = (data: unknown): LocationDetail[] => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
    return Object.entries(data).flatMap(([id, value]) => {
        if (!getPointType(id) || !value || typeof value !== 'object' || Array.isArray(value)) return [];
        const raw = value as Record<string, unknown>;
        const name = typeof raw.name === 'string' ? raw.name.trim() : '';
        if (!name || name.includes('^') || ![1, '1'].includes(raw.share as string | number)) return [];
        return [{
            ...raw,
            id,
            share: raw.share,
            name,
            country: text(raw.country), state: text(raw.state), city: text(raw.city),
            street: text(raw.street), number: text(raw.number), subname: text(raw.subname),
            title: text(raw.title), searchkeys: text(raw.searchkeys), group: text(raw.group),
            sort: text(raw.sort), date_of_installation: text(raw.date_of_installation),
            loclat: text(raw.loclat), loclon: text(raw.loclon),
            content: text(raw.content), voiceaddr: text(raw.voiceaddr),
        } as LocationDetail];
    });
};

export const getPointLabel = (point: LocationDetail): string =>
    text(point.subname) || text(point.title) || text(point.name) || point.id || '';

export const filterLocations = (locations: LocationDetail[], types: readonly PointType[], search: string): LocationDetail[] => {
    const { positive, negative, dateRanges, error } = parseLocationSearch(search);
    if (error) return [];
    return locations.filter(location => {
        const type = getPointType(location.id);
        if (!type || !types.includes(type)) return false;
        if (dateRanges.length) {
            const period = getInstallationPeriod(location.date_of_installation);
            if (!period || !dateRanges.every(range => period.start >= range.start && period.end <= range.end)) return false;
        }
        const content = [location.city, location.street, location.number, location.name,
            location.subname, location.title, location.searchkeys, location.state].join(' ').toLocaleLowerCase('pl');
        return positive.every(word => content.includes(word)) && negative.every(word => !content.includes(word));
    });
};

export const getCoordinates = (point: Pick<LocationDetail, 'loclat' | 'loclon'>): Coordinates | null => {
    if (!text(point.loclat) || !text(point.loclon)) return null;
    const lat = Number(point.loclat);
    const lng = Number(point.loclon);
    return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
        ? { lat, lng } : null;
};

export const getDistance = (a: Coordinates, b: Coordinates): number => {
    const radians = Math.PI / 180;
    const h = Math.sin((b.lat - a.lat) * radians / 2) ** 2
        + Math.cos(a.lat * radians) * Math.cos(b.lat * radians) * Math.sin((b.lng - a.lng) * radians / 2) ** 2;
    return 6371000 * 2 * Math.asin(Math.sqrt(Math.min(1, Math.max(0, h))));
};

export const getInstallationTimestamp = (value?: string): number | null => {
    const input = text(value);
    const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(input);
    const european = /^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/.exec(input);
    const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(input);
    const yearOnly = /^\d{4}$/.test(input);
    if (!iso && !european && !compact && !yearOnly) return null;
    const match = iso || compact;
    let year = Number(match ? match[1] : european ? european[3] : input);
    if (european && european[3].length === 2) year += 2000;
    const month = Number(match ? match[2] : european ? european[2] : 1);
    const day = Number(match ? match[3] : european ? european[1] : 1);
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
        ? date.getTime() : null;
};

interface InstallationDateRange { start: number; end: number; }
interface LocationSearch {
    positive: string[];
    negative: string[];
    dateRanges: InstallationDateRange[];
    error: 'invalidDate' | 'reversedDates' | null;
}

const getDatePeriod = (value: string): InstallationDateRange | null => {
    const match = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value);
    if (!match || Number(match[1]) === 0) return null;
    const [, year, month, day] = match;
    const start = getInstallationTimestamp(`${year}-${month || '01'}-${day || '01'}`);
    if (start === null) return null;
    const end = new Date(start);
    if (day) end.setUTCDate(end.getUTCDate() + 1);
    else if (month) end.setUTCMonth(end.getUTCMonth() + 1);
    else end.setUTCFullYear(end.getUTCFullYear() + 1);
    return { start, end: end.getTime() };
};

const getInstallationPeriod = (value?: string): InstallationDateRange | null => {
    const input = text(value);
    if (/^\d{4}$/.test(input)) return getDatePeriod(input);
    const start = getInstallationTimestamp(input);
    if (start === null) return null;
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 1);
    return { start, end: end.getTime() };
};

export const parseLocationSearch = (search: string): LocationSearch => {
    const result: LocationSearch = { positive: [], negative: [], dateRanges: [], error: null };
    const words = search.toLocaleLowerCase('pl').trim().split(/\s+/).filter(Boolean);
    for (const word of words) {
        if (word.startsWith('@')) {
            const parts = word.slice(1).split(/\.{2,3}|:/);
            const first = getDatePeriod(parts[0]);
            const last = parts.length === 2 ? getDatePeriod(parts[1]) : first;
            if (parts.length > 2 || !first || !last) {
                result.error = 'invalidDate';
                return result;
            }
            if (first.start >= last.end) {
                result.error = 'reversedDates';
                return result;
            }
            result.dateRanges.push({ start: first.start, end: last.end });
        } else if (word.startsWith('!')) {
            if (word.length > 1) result.negative.push(word.slice(1));
        } else {
            result.positive.push(word);
        }
    }
    return result;
};

export const formatDistance = (distance: number | null, locale: string): string | null => {
    if (distance === null || !Number.isFinite(distance) || distance < 0) return null;
    const kilometres = distance >= 1000;
    const key = `${locale}:${kilometres ? 'km' : 'm'}`;
    let formatter = distanceFormatters.get(key);
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, { maximumFractionDigits: kilometres ? 1 : 0 });
        distanceFormatters.set(key, formatter);
    }
    const value = formatter.format(kilometres ? distance / 1000 : distance);
    return `${value} ${kilometres ? 'km' : 'm'}`;
};

export const formatInstallationDate = (value: string | undefined, locale: string): string | null => {
    const timestamp = getInstallationTimestamp(value);
    if (timestamp === null) return null;
    if (/^\d{4}$/.test(text(value))) return text(value);
    let formatter = dateFormatters.get(locale);
    if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
        dateFormatters.set(locale, formatter);
    }
    return formatter.format(timestamp);
};

interface SortValues {
    label: string;
    distance: number | null;
    installationTimestamp: number | null;
    installationDate?: string;
}
export interface LocationObjectGroup extends SortValues {
    id: string;
    points: LocationDetail[];
}
export interface LocationCityGroup extends SortValues {
    id: string;
    city: string;
    total: number;
    objects: LocationObjectGroup[];
}

const compareGroups = (mode: LocationSort, a: SortValues, b: SortValues): number => {
    const labelOrder = collator.compare(a.label, b.label);
    if (mode === 'alphabetical') return labelOrder;
    const aValue = mode === 'distance' ? a.distance : a.installationTimestamp;
    const bValue = mode === 'distance' ? b.distance : b.installationTimestamp;
    if (aValue === null) return bValue === null ? labelOrder : 1;
    if (bValue === null) return -1;
    return (mode === 'distance' ? aValue - bValue : bValue - aValue) || labelOrder;
};

export const compareObjectPoints = (a: LocationDetail, b: LocationDetail): number => {
    const aSort = text(a.sort);
    const bSort = text(b.sort);
    if (aSort && !bSort) return -1;
    if (!aSort && bSort) return 1;
    return collator.compare(aSort, bSort) || collator.compare(getPointLabel(a), getPointLabel(b))
        || collator.compare(a.id || '', b.id || '');
};

export const getLocationObjectKey = (point: LocationDetail): string => JSON.stringify([
    JSON.stringify([text(point.city).toLocaleLowerCase('pl')]), text(point.name).toLocaleLowerCase('pl'),
    text(point.street).toLocaleLowerCase('pl'), text(point.number).split('#')[0].trim().toLocaleLowerCase('pl'), text(point.group),
]);

export const groupLocations = (
    locations: LocationDetail[], mode: LocationSort = 'alphabetical', position: Coordinates | null = null,
): LocationCityGroup[] => {
    const cities = new Map<string, LocationCityGroup>();
    const objects = new Map<string, LocationObjectGroup>();
    locations.forEach(point => {
        const city = text(point.city);
        const cityId = JSON.stringify([city.toLocaleLowerCase('pl')]);
        const streetNumber = text(point.number).split('#')[0].trim();
        const street = [text(point.street), streetNumber].filter(Boolean).join(' ');
        const label = [text(point.name), street].filter(Boolean).join(', ');
        const objectId = getLocationObjectKey(point);
        if (!cities.has(cityId)) {
            cities.set(cityId, { id: cityId, city, label: city, total: 0, objects: [], distance: null, installationTimestamp: null });
        }
        const cityGroup = cities.get(cityId)!;
        if (!objects.has(objectId)) {
            const object = { id: objectId, label, points: [], distance: null, installationTimestamp: null };
            objects.set(objectId, object);
            cityGroup.objects.push(object);
        }
        const objectGroup = objects.get(objectId)!;
        objectGroup.points.push(point);
        cityGroup.total++;
        const coords = getCoordinates(point);
        const distance = position && coords ? getDistance(position, coords) : null;
        const timestamp = getInstallationTimestamp(point.date_of_installation);
        [cityGroup, objectGroup].forEach(group => {
            if (distance !== null && (group.distance === null || distance < group.distance)) group.distance = distance;
            if (timestamp !== null && (group.installationTimestamp === null || timestamp > group.installationTimestamp)) {
                group.installationTimestamp = timestamp;
                group.installationDate = point.date_of_installation;
            }
        });
    });
    const result = Array.from(cities.values());
    result.forEach(city => {
        city.objects.sort((a, b) => compareGroups(mode, a, b));
        city.objects.forEach(object => object.points.sort(compareObjectPoints));
    });
    return result.sort((a, b) => {
        if (mode === 'alphabetical' && Boolean(a.city) !== Boolean(b.city)) return a.city ? -1 : 1;
        return compareGroups(mode, a, b);
    });
};
